import { ActionType, formatCurrency as formatDollars, PlacementId } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { Error } from "components/error";
import { InvoiceResend } from "components/invoices/invoice-resend";
import { ContentContainer, PageHeader, Stage } from "components/layout";
import { reportColors } from "components/reports/report-colors";
import { trpc } from "components/rpc/trpc";
import { defaultCardStyle } from "components/ui/card";
import { SortDown, SortUp } from "components/ui/icon";
import { defaultLinkStyle } from "components/ui/link";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { useGetClientQuery } from "generated/graphql";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useIsAllowed, useRegionId, useVisibleUsers } from "store/selectors/hooks";
import {
    outstandingInvoicesReportSlice,
    OutstandingInvoicesSortField,
} from "store/slices/outstanding-invoices-report";

type RouterOutput = inferRouterOutputs<AppRouter>;
type ReportData = RouterOutput["outstandingInvoices"]["outstandingInvoicesReport"];
type InvoiceRow = ReportData["invoices"][number];

type SortField = OutstandingInvoicesSortField;

const formatCurrency = (cents: number): string => {
    return formatDollars(cents / 100);
};

const sortInvoices = (
    invoices: InvoiceRow[],
    sortField: SortField,
    sortAsc: boolean,
): InvoiceRow[] => {
    return [...invoices].sort((a, b) => {
        if (
            sortField === "clientName" ||
            sortField === "communityName" ||
            sortField === "advisorName" ||
            sortField === "serial"
        ) {
            const aStr = (a[sortField] ?? "").toLowerCase();
            const bStr = (b[sortField] ?? "").toLowerCase();
            return sortAsc ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
        }
        if (sortField === "moveDate" || sortField === "dueDate") {
            const aStr = a[sortField] ?? "";
            const bStr = b[sortField] ?? "";
            return sortAsc ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
        }
        const aNum = a[sortField];
        const bNum = b[sortField];
        return sortAsc ? aNum - bNum : bNum - aNum;
    });
};

const SectionHeader: React.FC<{
    label: string;
    count: number;
    totalCents: number;
    accentColor: string;
}> = ({ label, count, totalCents, accentColor }) => (
    <div
        className={`flex items-center justify-between py-2 px-4 bg-gray-50 border-b border-gray-200`}>
        <span className={`text-sm font-semibold ${accentColor}`}>
            {label} ({count})
        </span>
        <span className={`text-sm font-semibold ${accentColor}`}>
            {formatCurrency(totalCents)}
        </span>
    </div>
);

const InvoiceTable: React.FC<{
    sortField: SortField;
    sortAsc: boolean;
    onSort: (field: SortField) => void;
    onSendInvoice: (inv: InvoiceRow) => void;
    sections: {
        key: string;
        label: string;
        accentColor: string;
        balanceColor: string;
        badgeClasses: string;
        badgeLabel: (inv: InvoiceRow) => string;
        showDot: boolean;
        invoices: InvoiceRow[];
        totalCents: number;
    }[];
}> = ({ sortField, sortAsc, onSort, onSendInvoice, sections }) => {
    const sortIndicator = (field: SortField) => {
        if (sortField !== field) return null;
        return sortAsc ? (
            <SortUp className="inline ml-1" />
        ) : (
            <SortDown className="inline ml-1" />
        );
    };

    const visibleSections = sections.filter(s => s.invoices.length > 0);
    if (visibleSections.length === 0) {
        return (
            <div className="text-center py-10 text-gray-500">
                No outstanding invoices found.
            </div>
        );
    }

    return (
        <div className={`${defaultCardStyle} overflow-auto`}>
            <table className="min-w-full">
                <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                        <th
                            className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                            onClick={() => onSort("clientName")}>
                            Client
                            {sortIndicator("clientName")}
                        </th>
                        <th
                            className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                            onClick={() => onSort("communityName")}>
                            Community
                            {sortIndicator("communityName")}
                        </th>
                        <th
                            className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                            onClick={() => onSort("advisorName")}>
                            Advisor
                            {sortIndicator("advisorName")}
                        </th>
                        <th
                            className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                            onClick={() => onSort("moveDate")}>
                            Move Date
                            {sortIndicator("moveDate")}
                        </th>
                        <th
                            className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                            onClick={() => onSort("serial")}>
                            Serial
                            {sortIndicator("serial")}
                        </th>
                        <th
                            className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                            onClick={() => onSort("amountCents")}>
                            Invoice Total
                            {sortIndicator("amountCents")}
                        </th>
                        <th
                            className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                            onClick={() => onSort("remainingBalanceCents")}>
                            Balance Due
                            {sortIndicator("remainingBalanceCents")}
                        </th>
                        <th
                            className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                            onClick={() => onSort("dueDate")}>
                            Due Date / Status
                            {sortIndicator("dueDate")}
                        </th>
                        <th className="py-3 px-4 text-xs font-semibold text-gray-600 uppercase" />
                    </tr>
                </thead>
                <tbody>
                    {visibleSections.map(section => (
                        <React.Fragment key={section.key}>
                            <tr>
                                <td colSpan={9} className="p-0">
                                    <SectionHeader
                                        label={section.label}
                                        count={section.invoices.length}
                                        totalCents={section.totalCents}
                                        accentColor={section.accentColor}
                                    />
                                </td>
                            </tr>
                            {section.invoices.map(inv => (
                                <tr
                                    key={inv.invoiceId}
                                    className="border-b border-gray-100 hover:bg-gray-50">
                                    <td className="py-2.5 px-4 text-sm">
                                        <Link
                                            to={`/clients/show/${inv.clientId}/invoices`}
                                            className={defaultLinkStyle}>
                                            {inv.clientName}
                                        </Link>
                                        {inv.bestContactName && (
                                            <div className="text-xs text-gray-500">
                                                {inv.bestContactName}
                                            </div>
                                        )}
                                    </td>
                                    <td className="py-2.5 px-4 text-sm">
                                        {inv.communityId ? (
                                            <Link
                                                to={`/communities/show/${inv.communityId}/invoices`}
                                                className={defaultLinkStyle}>
                                                {inv.communityName}
                                            </Link>
                                        ) : (
                                            <span className="text-gray-700">
                                                {inv.communityName}
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-2.5 px-4 text-sm text-gray-700">
                                        {inv.advisorName}
                                    </td>
                                    <td className="py-2.5 px-4 text-sm">
                                        {inv.moveDate ?? "—"}
                                    </td>
                                    <td className="py-2.5 px-4 text-sm font-mono">
                                        {inv.serial}
                                    </td>
                                    <td className="py-2.5 px-4 text-sm text-right tabular-nums text-gray-700">
                                        <div>{formatCurrency(inv.amountCents)}</div>
                                        {inv.installmentCount &&
                                            inv.installmentCount > 1 && (
                                                <div className="text-xs text-gray-500">
                                                    split &middot; {inv.installmentCount}{" "}
                                                    pmts
                                                </div>
                                            )}
                                    </td>
                                    <td
                                        className={`py-2.5 px-4 text-sm text-right font-semibold tabular-nums ${section.balanceColor}`}>
                                        <div>
                                            {formatCurrency(inv.remainingBalanceCents)}
                                        </div>
                                        {inv.overdueAmountCents > 0 &&
                                            inv.overdueAmountCents !==
                                                inv.remainingBalanceCents && (
                                                <div className="text-xs font-normal text-red-600">
                                                    {formatCurrency(
                                                        inv.overdueAmountCents,
                                                    )}{" "}
                                                    overdue
                                                </div>
                                            )}
                                    </td>
                                    <td className="py-2.5 px-4 text-sm">
                                        <div>{inv.dueDate ?? "—"}</div>
                                        <span
                                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold mt-0.5 ${section.badgeClasses}`}>
                                            {section.showDot && (
                                                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                            )}
                                            {section.badgeLabel(inv)}
                                        </span>
                                        {inv.category === "overdue" &&
                                            inv.nextInstallmentDueDate && (
                                                <div className="text-xs text-blue-600 mt-0.5">
                                                    next due {inv.nextInstallmentDueDate}
                                                </div>
                                            )}
                                    </td>
                                    <td className="py-2.5 px-4 text-sm">
                                        {inv.category === "new" &&
                                        inv.status === "new" ? (
                                            <SecondaryCompactButton
                                                selected
                                                onClick={() => onSendInvoice(inv)}>
                                                Send
                                            </SecondaryCompactButton>
                                        ) : (
                                            <SecondaryCompactButton
                                                onClick={() => onSendInvoice(inv)}>
                                                Resend
                                            </SecondaryCompactButton>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

type CommunitySortField = "communityName" | "overdueCount" | "overdueTotalCents";
type OverdueByCommunityRow = ReportData["overdueByCommunity"][number];

const sortCommunities = (
    rows: OverdueByCommunityRow[],
    field: CommunitySortField,
    asc: boolean,
): OverdueByCommunityRow[] => {
    return [...rows].sort((a, b) => {
        if (field === "communityName") {
            const aStr = a.communityName.toLowerCase();
            const bStr = b.communityName.toLowerCase();
            return asc ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
        }
        return asc ? a[field] - b[field] : b[field] - a[field];
    });
};

const OverdueByCommunityTable: React.FC<{
    data: OverdueByCommunityRow[];
}> = ({ data }) => {
    const [sortField, setSortField] = useState<CommunitySortField>("overdueTotalCents");
    const [sortAsc, setSortAsc] = useState(false);

    const handleSort = (field: CommunitySortField) => {
        if (sortField === field) {
            setSortAsc(!sortAsc);
        } else {
            setSortField(field);
            setSortAsc(false);
        }
    };

    const sorted = useMemo(
        () => sortCommunities(data, sortField, sortAsc),
        [data, sortField, sortAsc],
    );

    const sortIndicator = (field: CommunitySortField) => {
        if (sortField !== field) return null;
        return sortAsc ? (
            <SortUp className="inline ml-1" />
        ) : (
            <SortDown className="inline ml-1" />
        );
    };

    return (
        <div className={`${defaultCardStyle} overflow-auto mb-6`}>
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <span className="text-sm font-semibold text-red-700">
                    Overdue by Community
                </span>
            </div>
            <table className="min-w-full">
                <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                        <th
                            className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort("communityName")}>
                            Community
                            {sortIndicator("communityName")}
                        </th>
                        <th
                            className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort("overdueCount")}>
                            Overdue Count
                            {sortIndicator("overdueCount")}
                        </th>
                        <th
                            className="py-3 px-4 text-right text-xs font-semibold text-gray-600 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort("overdueTotalCents")}>
                            Overdue Amount
                            {sortIndicator("overdueTotalCents")}
                        </th>
                        <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase whitespace-nowrap">
                            Oldest Overdue
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map(row => (
                        <tr
                            key={row.communityId}
                            className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-2.5 px-4 text-sm whitespace-nowrap">
                                {row.communityId ? (
                                    <Link
                                        to={`/communities/show/${row.communityId}/invoices`}
                                        className={defaultLinkStyle}>
                                        {row.communityName}
                                    </Link>
                                ) : (
                                    <span className="text-gray-700">
                                        {row.communityName}
                                    </span>
                                )}
                            </td>
                            <td className="py-2.5 px-4 text-sm text-right tabular-nums text-red-700 whitespace-nowrap">
                                {row.overdueCount}
                            </td>
                            <td className="py-2.5 px-4 text-sm text-right font-semibold tabular-nums text-red-700 whitespace-nowrap">
                                {formatCurrency(row.overdueTotalCents)}
                            </td>
                            <td className="py-2.5 px-4 text-sm text-gray-700 whitespace-nowrap">
                                {row.oldestOverdueDate ?? "—"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

type CashFlowBucket = ReportData["cashFlowOutlook"][number];

const bucketBarClass = (color: string): string => {
    return color === "red" ? "bg-red-500" : "bg-blue-500";
};

const computeYTicks = (maxCents: number): number[] => {
    if (maxCents <= 0) return [0];
    const maxDollars = maxCents / 100;
    const rawStep = maxDollars / 5;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const nice = [1, 2, 2.5, 5, 10];
    const step = magnitude * (nice.find(n => n * magnitude >= rawStep) ?? 10);
    const ticks: number[] = [];
    for (let v = 0; v <= maxDollars * 1.1; v += step) {
        ticks.push(Math.round(v));
    }
    if (ticks.length < 2) ticks.push(step);
    return ticks;
};

const formatCompactDollars = (dollars: number): string => {
    if (dollars >= 1000) {
        const k = dollars / 1000;
        return `$${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
    }
    return `$${dollars.toLocaleString()}`;
};

const CHART_HEIGHT = 160;

const CashFlowChart: React.FC<{ buckets: CashFlowBucket[] }> = ({ buckets }) => {
    const maxCents = Math.max(...buckets.map(b => b.totalCents), 1);
    const yTicks = computeYTicks(maxCents);
    const yMax = yTicks[yTicks.length - 1] * 100;

    return (
        <div className={`${defaultCardStyle} mb-6 overflow-hidden`}>
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                <span className="text-sm font-semibold text-gray-700">
                    Cash Flow Outlook
                </span>
            </div>
            <div className="p-4">
                <div className="flex">
                    <div
                        className="flex flex-col justify-between pr-2 text-right"
                        style={{
                            height: CHART_HEIGHT,
                            minWidth: "48px",
                        }}>
                        {[...yTicks].reverse().map(tick => (
                            <span
                                key={tick}
                                className="text-xs text-gray-400 leading-none">
                                {formatCompactDollars(tick)}
                            </span>
                        ))}
                    </div>
                    <div className="flex-1 flex flex-col">
                        <div
                            className="flex items-end gap-3 border-l border-b border-gray-200"
                            style={{ height: CHART_HEIGHT }}>
                            {buckets.map(bucket => {
                                const pct =
                                    yMax > 0 ? (bucket.totalCents / yMax) * 100 : 0;
                                return (
                                    <div
                                        key={bucket.label}
                                        className="flex-1 flex flex-col items-center justify-end h-full">
                                        <span className="text-xs text-gray-600 font-medium mb-0.5">
                                            {formatCurrency(bucket.totalCents)}
                                        </span>
                                        {bucket.totalCents > 0 && (
                                            <div
                                                className={`w-10 ${bucketBarClass(bucket.color)} rounded-t`}
                                                style={{
                                                    height: `${pct}%`,
                                                    minHeight: "4px",
                                                }}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex gap-3 mt-1">
                            {buckets.map(bucket => (
                                <div key={bucket.label} className="flex-1 text-center">
                                    <span className="text-xs text-gray-500">
                                        {bucket.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const OutstandingInvoices: React.FC = () => {
    const regionId = useRegionId();
    const dispatch = useAppDispatch();
    const reportState = useAppSelector(state => state.outstandingInvoicesReport);
    const canViewAllInvoices = useIsAllowed(ActionType.ViewAllInvoices);
    const canViewReports = useIsAllowed(ActionType.ViewReports);
    const canViewAll = canViewAllInvoices || canViewReports;
    const users = useVisibleUsers();

    const updateState = (partial: Partial<typeof reportState>) => {
        dispatch(outstandingInvoicesReportSlice.actions.update(partial));
    };

    const trpcClient = trpc.useTRPC();

    const reportQuery = useQuery(
        trpcClient.outstandingInvoices.outstandingInvoicesReport.queryOptions(
            {
                regionId: regionId || "",
                advisorId: reportState.advisorId,
            },
            { enabled: !!regionId },
        ),
    );

    const sortField = reportState.sortField;
    const sortAsc = reportState.sortAsc;

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            updateState({ sortAsc: !sortAsc });
        } else {
            updateState({ sortField: field, sortAsc: false });
        }
    };

    const sections = useMemo(() => {
        if (!reportQuery.data) return [];
        const invoices = reportQuery.data.invoices;
        const overdue = sortInvoices(
            invoices.filter(i => i.category === "overdue"),
            sortField,
            sortAsc,
        );
        const upcoming = sortInvoices(
            invoices.filter(i => i.category === "upcoming"),
            sortField,
            sortAsc,
        );
        const newInvoices = sortInvoices(
            invoices.filter(i => i.category === "new"),
            sortField,
            sortAsc,
        );

        return [
            {
                key: "overdue",
                label: "Overdue",
                accentColor: "text-red-700",
                balanceColor: "text-red-700",
                badgeClasses: "bg-red-100 text-red-700",
                badgeLabel: (inv: InvoiceRow) => `${inv.daysOverdue}d overdue`,
                showDot: false,
                invoices: overdue,
                totalCents: reportQuery.data.summary.totalOverdueCents,
            },
            {
                key: "upcoming",
                label: "Upcoming",
                accentColor: reportColors.upcoming.text,
                balanceColor: reportColors.upcoming.text,
                badgeClasses: reportColors.upcoming.badge,
                badgeLabel: () => "\u2713 Sent",
                showDot: false,
                invoices: upcoming,
                totalCents: reportQuery.data.summary.totalUpcomingCents,
            },
            {
                key: "new",
                label: "New / Unsent",
                accentColor: "text-amber-700",
                balanceColor: "text-amber-700",
                badgeClasses: "bg-amber-100 text-amber-700",
                badgeLabel: () => "Not yet sent",
                showDot: true,
                invoices: newInvoices,
                totalCents: reportQuery.data.summary.totalNewCents,
            },
        ];
    }, [reportQuery.data, sortField, sortAsc]);

    const [sendingInvoiceInfo, setSendingInvoiceInfo] = useState<{
        invoiceId: string;
        clientId: string;
        serial: string;
    } | null>(null);

    const clientQuery = useGetClientQuery(
        { id: (sendingInvoiceInfo?.clientId ?? "") as PlacementId },
        { enabled: !!sendingInvoiceInfo },
    );

    const sendingInvoice = useMemo(() => {
        if (!sendingInvoiceInfo || !clientQuery.data?.getClient?.invoices) return null;
        return clientQuery.data.getClient.invoices.find(
            inv => inv.id === sendingInvoiceInfo.invoiceId,
        );
    }, [sendingInvoiceInfo, clientQuery.data]);

    const handleSendInvoice = (inv: InvoiceRow) => {
        setSendingInvoiceInfo({
            invoiceId: inv.invoiceId,
            clientId: inv.clientId,
            serial: inv.serial,
        });
    };

    const handleDismissSend = () => {
        setSendingInvoiceInfo(null);
    };

    const handleSendRefetch = () => {
        reportQuery.refetch();
        setSendingInvoiceInfo(null);
    };

    const userOptions = useMemo(() => {
        return Object.values(users)
            .map(u => ({ id: u.id, name: u.name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [users]);

    return (
        <Stage>
            <PageHeader title="Outstanding Invoices" stackBreakpoint="md">
                {canViewAll && (
                    <div className="w-full sm:w-64">
                        <Select
                            label=""
                            value={reportState.advisorId ?? "all"}
                            onChange={e =>
                                updateState({
                                    advisorId:
                                        e.target.value === "all"
                                            ? undefined
                                            : e.target.value,
                                })
                            }>
                            <option value="all">All Advisors</option>
                            {userOptions.map(u => (
                                <option key={u.id} value={u.id}>
                                    {u.name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </PageHeader>
            <ContentContainer>
                {reportQuery.isLoading && (
                    <div className="flex justify-center py-12">
                        <Spinner />
                    </div>
                )}

                {reportQuery.isError && (
                    <Error componentName="OutstandingInvoices">
                        Failed to load report. Please try again.
                    </Error>
                )}

                {reportQuery.data && (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-gray-500 uppercase font-medium">
                                    Total Outstanding
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {formatCurrency(
                                        reportQuery.data.summary.totalOutstandingCents,
                                    )}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {reportQuery.data.summary.overdueCount +
                                        reportQuery.data.summary.upcomingCount +
                                        reportQuery.data.summary.newCount}{" "}
                                    invoices
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-red-700 uppercase font-medium">
                                    Total Overdue
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {formatCurrency(
                                        reportQuery.data.summary.totalOverdueCents,
                                    )}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {reportQuery.data.summary.overdueCount} invoices
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div
                                    className={`text-xs ${reportColors.upcoming.text} uppercase font-medium`}>
                                    Total Upcoming
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {formatCurrency(
                                        reportQuery.data.summary.totalUpcomingCents,
                                    )}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {reportQuery.data.summary.upcomingCount} invoices
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-amber-700 uppercase font-medium">
                                    Total New / Unsent
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {formatCurrency(
                                        reportQuery.data.summary.totalNewCents,
                                    )}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {reportQuery.data.summary.newCount} invoices
                                </div>
                            </div>
                            <div className={`${defaultCardStyle} p-3`}>
                                <div className="text-xs text-gray-500 uppercase font-medium">
                                    Avg. Days Overdue
                                </div>
                                <div className="text-2xl font-bold mt-1">
                                    {reportQuery.data.summary.avgDaysOverdue ?? "—"}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {reportQuery.data.summary.overdueCount > 0
                                        ? `across ${reportQuery.data.summary.overdueCount} invoices`
                                        : "no overdue invoices"}
                                </div>
                            </div>
                        </div>

                        {reportQuery.data.cashFlowOutlook.length > 0 && (
                            <CashFlowChart buckets={reportQuery.data.cashFlowOutlook} />
                        )}

                        {reportQuery.data.overdueByCommunity.length > 0 && (
                            <OverdueByCommunityTable
                                data={reportQuery.data.overdueByCommunity}
                            />
                        )}

                        <InvoiceTable
                            sortField={sortField}
                            sortAsc={sortAsc}
                            onSort={handleSort}
                            onSendInvoice={handleSendInvoice}
                            sections={sections}
                        />

                        <div className="text-xs text-gray-400 text-center py-4">
                            Showing invoices with status New or Sent &middot; Paid and
                            Void invoices excluded
                        </div>

                        <ResponsivePanel
                            headerText={
                                sendingInvoiceInfo
                                    ? `Send Invoice ${sendingInvoiceInfo.serial}`
                                    : ""
                            }
                            isOpen={!!sendingInvoiceInfo}
                            onDismiss={handleDismissSend}
                            type={PanelType.extraLarge}>
                            {sendingInvoice ? (
                                <InvoiceResend
                                    refetch={handleSendRefetch}
                                    invoice={sendingInvoice}
                                    onCancel={handleDismissSend}
                                />
                            ) : sendingInvoiceInfo ? (
                                <div className="flex justify-center py-12">
                                    <Spinner />
                                </div>
                            ) : null}
                        </ResponsivePanel>
                    </>
                )}
            </ContentContainer>
        </Stage>
    );
};
